// src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';

// FIX 1: Changed fallback port from 3001 to 3000. 
const API_BASE = "/api/v1";
const TOKEN_KEY = "miraal_token";

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  mobile?: string;
  country?: string;
  pincode?: string;
  provider?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  logout: () => void;
  signup: (name: string, email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  guestLogin: (name: string) => Promise<boolean>;
  forgotPassword: (email: string) => Promise<{ ok: boolean; error?: string }>;
  resetPassword: (token: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  updateUser: (updates: Partial<User>) => void;
  authError: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// FastAPI's own validation errors put `detail` as an array of {msg, loc, ...}
// objects, not a string - rendering that directly produces "[object Object]"
// (or worse, misleads the user into thinking it's a different failure, like
// signup showing "email already exists" for what was actually a missing
// field). This normalizes every shape the backend can send into one string.
function extractErrorMessage(json: any, fallback: string): string {
  const detail = json?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((d: any) => d?.msg).filter(Boolean).join('; ') || fallback;
  }
  return json?.error?.message || fallback;
}

function mapBackendUser(u: any): User {
  return {
    id: u.id,
    email: u.email,
    firstName: u.first_name || u.firstName || '',
    lastName: u.last_name || u.lastName || '',
    provider: 'email',
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    const restoreSession = async () => {
      // Google OAuth's callback is a server-side redirect and can never
      // write to localStorage directly - it passes tokens via URL query
      // params instead. Catch them here, store them the same way a normal
      // login does, and clean the URL so they don't linger in history or
      // get bookmarked. After this, the existing localStorage-based check
      // below picks the token up naturally - no duplicated fetch logic.
      const urlParams = new URLSearchParams(window.location.search);
      const oauthToken = urlParams.get("google_auth_token");
      const oauthRefresh = urlParams.get("google_auth_refresh");
      if (oauthToken) {
        localStorage.setItem(TOKEN_KEY, oauthToken);
        if (oauthRefresh) {
          localStorage.setItem("miraal_refresh_token", oauthRefresh);
        }
        window.history.replaceState({}, "", window.location.pathname);
      }

      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        const json = await res.json();
        // FIX 2: Safely check for user data in multiple response shapes
        const userData = json.data?.user || json.user || json.data;
        if (res.ok && userData?.id) {
          setUser(mapBackendUser(userData));
        } else {
          localStorage.removeItem(TOKEN_KEY);
        }
      } catch {
        localStorage.removeItem(TOKEN_KEY);
      } finally {
        setIsLoading(false);
      }
    };
    restoreSession();
  }, []);

  const login = async (email: string, password: string): Promise<{ ok: boolean; error?: string }> => {
    setIsLoading(true);
    setAuthError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const json = await res.json();

      // FIX 3: Check for token in both json.data.token and json.token
      const token = json.data?.token || json.token;
      const userData = json.data?.user || json.user;

      if (!res.ok || !token) {
        const error = extractErrorMessage(json, 'Invalid email or password');
        setAuthError(error);
        setIsLoading(false);
        return { ok: false, error };
      }

      localStorage.setItem(TOKEN_KEY, token);
      if (json.data?.refreshToken || json.refreshToken) {
        localStorage.setItem("miraal_refresh_token", json.data?.refreshToken || json.refreshToken);
      }

      if (userData) setUser(mapBackendUser(userData));
      setIsLoading(false);
      return { ok: true };
    } catch (err: any) {
      const error = err.message || 'Login failed. Please try again.';
      setAuthError(error);
      setIsLoading(false);
      return { ok: false, error };
    }
  };

  const signup = async (name: string, email: string, password: string): Promise<{ ok: boolean; error?: string }> => {
    setIsLoading(true);
    setAuthError(null);
    try {
      // Backend's real contract is firstName/lastName (used throughout, e.g.
      // Settings, useUserProfile) - the signup form only collects one "Full
      // Name" field, so split it here rather than changing the more widely
      // depended-on backend shape. A single-word name becomes firstName with
      // an empty lastName, which the backend already accepts (lastName has
      // a default).
      const [firstName, ...rest] = name.trim().split(/\s+/);
      const lastName = rest.join(' ');
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ firstName, lastName, email, password }),
      });
      const json = await res.json();

      const token = json.data?.token || json.token;
      const userData = json.data?.user || json.user;

      if (!res.ok || !token) {
        const error = extractErrorMessage(json, 'Could not create account.');
        setAuthError(error);
        setIsLoading(false);
        return { ok: false, error };
      }

      localStorage.setItem(TOKEN_KEY, token);
      if (json.data?.refreshToken || json.refreshToken) {
        localStorage.setItem("miraal_refresh_token", json.data?.refreshToken || json.refreshToken);
      }
      if (userData) setUser(mapBackendUser(userData));
      setIsLoading(false);
      return { ok: true };
    } catch (err: any) {
      const error = err.message || 'Signup failed. Please try again.';
      setAuthError(error);
      setIsLoading(false);
      return { ok: false, error };
    }
  };

  const guestLogin = async (name: string): Promise<boolean> => {
    setIsLoading(true);
    setAuthError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name }),
      });
      const json = await res.json();

      const token = json.data?.token || json.token;
      const userData = json.data?.user || json.user;

      if (!res.ok || !token) {
        console.error('AUTH_DEBUG guestLogin response:', json);
        setAuthError(json.detail || json.error?.message || 'Guest login failed.');
        setIsLoading(false);
        return false;
      }

      localStorage.setItem(TOKEN_KEY, token);
      if (json.data?.refreshToken || json.refreshToken) {
        localStorage.setItem("miraal_refresh_token", json.data?.refreshToken || json.refreshToken);
      }
      if (userData) setUser(mapBackendUser(userData));
      setIsLoading(false);
      return true;
    } catch (err: any) {
      setAuthError(err.message || 'Guest login failed.');
      setIsLoading(false);
      return false;
    }
  };

  const forgotPassword = async (email: string): Promise<{ ok: boolean; error?: string }> => {
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const json = await res.json();
      if (!res.ok) return { ok: false, error: extractErrorMessage(json, 'Could not send reset link.') };
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err.message || 'Could not send reset link.' };
    }
  };

  const resetPassword = async (token: string, password: string): Promise<{ ok: boolean; error?: string }> => {
    try {
      const res = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const json = await res.json();
      if (!res.ok) return { ok: false, error: extractErrorMessage(json, 'Could not reset password.') };
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err.message || 'Could not reset password.' };
    }
  };

  const logout = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      }).catch(() => {});
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  const updateUser = (updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        signup,
        guestLogin,
        forgotPassword,
        resetPassword,
        updateUser,
        authError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};